import { apiFetch } from "@/lib/apiClient";
import {
  queryOptions,
  useQuery,
  type UseQueryOptions,
} from "@tanstack/react-query";
import type {
  JobsResponseDto,
  RecommendedJobsResponseDto,
} from "@artha/api-contracts";
import type {
  InsightsActivity,
  InsightsJobs,
  InsightsOverview,
  InsightsQuery,
} from "@artha/types";

export type InsightsQueryParams = InsightsQuery;

/** Converts the shared insights query DTO into a URL query string. */
const buildInsightsQueryString = ({
  rangePreset,
  startDate,
  endDate,
  payoutType,
}: InsightsQueryParams) => {
  const params = new URLSearchParams();

  if (rangePreset) {
    params.set("rangePreset", rangePreset);
  }

  if (startDate && endDate) {
    params.set("startDate", startDate);
    params.set("endDate", endDate);
  }

  if (payoutType) {
    params.set("payoutType", payoutType);
  }

  return params.toString();
};

/** Fetches one insights resource and unwraps the API response payload. */
const fetchInsights = async <T>(
  path: string,
  query: InsightsQueryParams,
): Promise<T> => {
  const queryString = buildInsightsQueryString(query);
  const response = await apiFetch<T>(
    `${path}${queryString ? `?${queryString}` : ""}`,
  );

  if (!response.ok) {
    throw new Error("Failed to fetch insights");
  }

  const payload = response.data;

  if (
    payload &&
    typeof payload === "object" &&
    "data" in payload &&
    payload.data !== undefined
  ) {
    return payload.data as T;
  }

  return payload as T;
};

export const insightsKeys = {
  all: ["insights"] as const,
  overview: (query: InsightsQueryParams) =>
    [...insightsKeys.all, "overview", query] as const,
  jobs: (query: InsightsQueryParams) =>
    [...insightsKeys.all, "jobs", query] as const,
  recommendedJobs: (query: InsightsQueryParams) =>
    [...insightsKeys.all, "recommendedJobs", query] as const,
  activity: (query: InsightsQueryParams) =>
    [...insightsKeys.all, "activity", query] as const,
};

/** Returns the TanStack Query config for the overview endpoint. */
export const insightsOverviewQueryOptions = (query: InsightsQueryParams) =>
  queryOptions<InsightsOverview>({
    queryKey: insightsKeys.overview(query),
    queryFn: () => fetchInsights<InsightsOverview>("/insights/overview", query),
  });

/** Returns the TanStack Query config for the jobs endpoint. */
export const insightsJobsQueryOptions = (query: InsightsQueryParams) =>
  queryOptions<InsightsJobs>({
    queryKey: insightsKeys.jobs(query),
    queryFn: () => fetchInsights<InsightsJobs>("/insights/jobs", query),
  });

/** Returns the TanStack Query config for the activity endpoint. */
export const insightsActivityQueryOptions = (query: InsightsQueryParams) =>
  queryOptions<InsightsActivity>({
    queryKey: insightsKeys.activity(query),
    queryFn: () => fetchInsights<InsightsActivity>("/insights/activity", query),
  });

/** Returns the TanStack Query config for the recommended jobs endpoint. */
export const insightsRecommendedJobsQueryOptions = (
  query: InsightsQueryParams,
) =>
  queryOptions<RecommendedJobsResponseDto>({
    queryKey: insightsKeys.recommendedJobs(query),
    queryFn: () =>
      fetchInsights<RecommendedJobsResponseDto>(
        "/insights/recommended-jobs",
        query,
      ),
  });

/** Hook wrapper for loading overview data. */
export const useInsightsOverviewQuery = (
  query: InsightsQueryParams,
  options?: Omit<UseQueryOptions<InsightsOverview>, "queryKey" | "queryFn">,
) =>
  useQuery({
    ...insightsOverviewQueryOptions(query),
    ...options,
  });

/** Hook wrapper for loading jobs data. */
export const useInsightsJobsQuery = (
  query: InsightsQueryParams,
  options?: Omit<UseQueryOptions<InsightsJobs>, "queryKey" | "queryFn">,
) =>
  useQuery({
    ...insightsJobsQueryOptions(query),
    ...options,
  });

/** Hook wrapper for loading activity data. */
export const useInsightsActivityQuery = (
  query: InsightsQueryParams,
  options?: Omit<UseQueryOptions<InsightsActivity>, "queryKey" | "queryFn">,
) =>
  useQuery({
    ...insightsActivityQueryOptions(query),
    ...options,
  });

/** Hook wrapper for loading dashboard recommended jobs. */
export const useInsightsRecommendedJobsQuery = (
  query: InsightsQueryParams,
  options?: Omit<
    UseQueryOptions<RecommendedJobsResponseDto>,
    "queryKey" | "queryFn"
  >,
) =>
  useQuery({
    ...insightsRecommendedJobsQueryOptions(query),
    ...options,
  });

/** Returns the TanStack Query config for the public profile recommended jobs endpoint. */
export const publicRecommendedJobsQueryOptions = (username: string) =>
  queryOptions<JobsResponseDto>({
    queryKey: ["publicRecommendedJobs", username],
    queryFn: async () => {
      const response = await apiFetch<JobsResponseDto>(
        `/public/${username}/recommended-jobs`,
      );
      if (!response.ok) {
        throw new Error("Failed to fetch public recommended jobs");
      }

      const payload = response.data;
      if (
        payload &&
        typeof payload === "object" &&
        "data" in payload &&
        payload.data !== undefined
      ) {
        return payload.data as JobsResponseDto;
      }

      return payload as JobsResponseDto;
    },
  });

/** Hook wrapper for loading public recommended jobs. */
export const usePublicRecommendedJobsQuery = (
  username: string,
  options?: Omit<UseQueryOptions<JobsResponseDto>, "queryKey" | "queryFn">,
) =>
  useQuery({
    ...publicRecommendedJobsQueryOptions(username),
    ...options,
  });
